import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, User, AlertCircle, CheckCircle, Phone, MessageSquare, Gift } from 'lucide-react';
import { cookieManager } from '../../utils/cookieManager.js';
import { getApiBaseUrl } from '../../utils/apiConfig.js';
import Footer from '../shared/Footer';

const API_BASE_URL = getApiBaseUrl();

const validateUsername = (username) => {
  const trimmed = username.trim();
  if (!trimmed) return '请输入用户名';
  if (trimmed.length < 3) return '用户名至少需要3个字符';
  if (trimmed.length > 50) return '用户名不能超过50个字符';
  return '';
};

const validatePassword = (password) => {
  if (!password) return '请输入密码';
  if (password.length < 8) return '密码至少需要8个字符';
  if (password.length > 100) return '密码不能超过100个字符';
  if (!/[A-Z]/.test(password)) return '密码必须包含至少一个大写字母';
  if (!/[a-z]/.test(password)) return '密码必须包含至少一个小写字母';
  if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?\\\"]/.test(password)) return '密码必须包含至少一个特殊字符';
  return '';
};

const validatePhone = (phone) => {
  const trimmed = phone.trim();
  if (!trimmed) return '请输入手机号';
  if (!/^1\d{10}$/.test(trimmed)) return '请输入11位中国大陆手机号';
  return '';
};

const validateSmsCode = (code) => {
  const trimmed = code.trim();
  if (!trimmed) return '请输入短信验证码';
  if (!/^\d{4,6}$/.test(trimmed)) return '验证码格式不正确';
  return '';
};

const validateInvitationCode = (code) => {
  if (!code) return '';  // 邀请码是可选的
  const trimmed = code.trim().toUpperCase();
  if (trimmed.length !== 8) return '邀请码长度应为8位';
  if (!/^[A-Z0-9]+$/.test(trimmed)) return '邀请码只能包含大写字母和数字';
  return '';
};

const RegisterForm = ({ onNavigateToLogin, onRegisterSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 从 URL 参数读取邀请码（如果存在）
  const urlInvitationCode = searchParams.get('invitation_code') || '';

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    phone: '',
    invitationCode: urlInvitationCode.toUpperCase().trim(),
    smsCode: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [smsMessage, setSmsMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // 🆕 监听 URL 参数变化，动态更新邀请码
  useEffect(() => {
    const invCode = searchParams.get('invitation_code');
    if (invCode && invCode.trim()) {
      setFormData(prev => ({
        ...prev,
        invitationCode: invCode.toUpperCase().trim()
      }));
    }
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const usernameError = validateUsername(formData.username);
    if (usernameError) newErrors.username = usernameError;

    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = '请确认密码';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = '两次输入的密码不一致';
    }

    const phoneError = validatePhone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;

    // 邀请码验证（可选字段）
    const invitationCodeError = validateInvitationCode(formData.invitationCode);
    if (invitationCodeError) newErrors.invitationCode = invitationCodeError;

    const smsError = validateSmsCode(formData.smsCode);
    if (smsError) newErrors.smsCode = smsError;

    if (!agreedToTerms) {
      newErrors.terms = '请阅读并同意用户服务协议';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendSmsCode = async () => {
    const phoneError = validatePhone(formData.phone);
    if (phoneError) {
      setErrors(prev => ({ ...prev, phone: phoneError }));
      return;
    }

    setIsSendingCode(true);
    setSmsMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: formData.phone, scene: 'register' })
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data.detail || data.message || '验证码发送失败';
        setErrors(prev => ({ ...prev, smsCode: message }));
        return;
      }

      setSmsMessage('验证码已发送，请注意查收');
      setCountdown(data.interval || 60);
    } catch (error) {
      setErrors(prev => ({ ...prev, smsCode: '验证码发送失败，请稍后再试' }));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors(prev => ({ ...prev, general: '' }));

    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: formData.username.trim(),
          password: formData.password,
          phone: formData.phone.trim(),
          sms_code: formData.smsCode.trim(),
          invitation_code: formData.invitationCode.trim() || undefined
        })
      });

      const result = await response.json();

      if (!response.ok) {
        setErrors(prev => ({ ...prev, general: result.detail || result.message || '注册失败' }));
        return;
      }

      try {
        const loginResponse = await fetch(`${API_BASE_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            username: formData.username.trim(),
            password: formData.password
          })
        });

        if (loginResponse.ok) {
          const loginResult = await loginResponse.json();
          const username = loginResult.user?.username || formData.username.trim();
          const userId = result._id;

          cookieManager.setAuthInfo(null, username, true);
          if (userId) {
            cookieManager.setCookie('userId', userId, 7);
          }

          if (onRegisterSuccess) onRegisterSuccess();
          navigate('/');
          return;
        }

        setErrors(prev => ({ ...prev, general: '注册成功，但自动登录失败，请手动登录' }));
      } catch (loginError) {
        setErrors(prev => ({ ...prev, general: '注册成功，但自动登录失败，请手动登录' }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, general: '网络错误，请稍后重试' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigateToLogin = () => {
    navigate('/auth/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      <div className="max-w-md w-full">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">用户注册</h1>
          <p className="text-gray-600">创建您的账户</p>
        </div>
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                用户名
                <span className="text-xs text-gray-500 ml-1">(3-50个字符)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={e => handleInputChange('username', e.target.value)}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${errors.username ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="请输入用户名"
                  disabled={isLoading}
                  maxLength={50}
                />
              </div>
              {errors.username && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.username}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                手机号
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={e => handleInputChange('phone', e.target.value)}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="请输入手机号码"
                  disabled={isLoading}
                  maxLength={11}
                />
              </div>
              {errors.phone && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.phone}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="invitationCode" className="block text-sm font-medium text-gray-700 mb-2">
                邀请码
                <span className="text-xs text-gray-500 ml-1">(可选)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Gift className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="invitationCode"
                  type="text"
                  value={formData.invitationCode}
                  onChange={e => handleInputChange('invitationCode', e.target.value.toUpperCase())}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${errors.invitationCode ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="如有邀请码请输入，享受更多优惠"
                  disabled={isLoading}
                  maxLength={8}
                />
              </div>
              {errors.invitationCode && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.invitationCode}
                </p>
              )}
              {!errors.invitationCode && formData.invitationCode && (
                <p className="mt-2 text-sm text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  邀请码已填入
                </p>
              )}
            </div>

            <div>
              <label htmlFor="smsCode" className="block text-sm font-medium text-gray-700 mb-2">
                短信验证码
              </label>
              <div className="flex space-x-4">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MessageSquare className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="smsCode"
                    type="text"
                    value={formData.smsCode}
                    onChange={e => handleInputChange('smsCode', e.target.value)}
                    className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${errors.smsCode ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                    placeholder="输入验证码"
                    disabled={isLoading}
                    maxLength={6}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSendSmsCode}
                  disabled={isSendingCode || countdown > 0 || isLoading}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isSendingCode || countdown > 0 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                >
                  {countdown > 0 ? `${countdown}s` : '发送验证码'}
                </button>
              </div>
              {smsMessage && !errors.smsCode && (
                <p className="mt-2 text-sm text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {smsMessage}
                </p>
              )}
              {errors.smsCode && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.smsCode}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                密码
                <span className="text-xs text-gray-500 ml-1">(8-100个字符)</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={e => handleInputChange('password', e.target.value)}
                  className={`block w-full px-3 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="请输入密码"
                  disabled={isLoading}
                  minLength={8}
                  maxLength={100}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" /> : <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.password}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                确认密码
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={e => handleInputChange('confirmPassword', e.target.value)}
                  className={`block w-full px-3 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="请再次输入密码"
                  disabled={isLoading}
                  maxLength={100}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={isLoading}
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" /> : <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />}
                </button>
                {formData.confirmPassword && formData.password === formData.confirmPassword && !errors.confirmPassword && (
                  <div className="absolute inset-y-0 right-10 pr-3 flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                )}
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 mb-2">密码要求：</h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li className="flex items-center">
                  <span className={`w-2 h-2 rounded-full mr-2 ${formData.password.length >= 8 ? 'bg-green-500' : 'bg-gray-300'}`} />
                  至少8个字符
                </li>
                <li className="flex items-center">
                  <span className={`w-2 h-2 rounded-full mr-2 ${formData.password.length <= 100 ? 'bg-green-500' : 'bg-red-500'}`} />
                  不超过100个字符
                </li>
                <li className="flex items-center">
                  <span className={`w-2 h-2 rounded-full mr-2 ${/[A-Z]/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300'}`} />
                  包含至少一个大写字母
                </li>
                <li className="flex items-center">
                  <span className={`w-2 h-2 rounded-full mr-2 ${/[a-z]/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300'}`} />
                  包含至少一个小写字母
                </li>
                <li className="flex items-center">
                  <span className={`w-2 h-2 rounded-full mr-2 ${/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?\\\"]/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300'}`} />
                  包含至少一个特殊字符
                </li>
                <li className="flex items-center">
                  <span className={`w-2 h-2 rounded-full mr-2 ${formData.password && formData.password === formData.confirmPassword ? 'bg-green-500' : 'bg-gray-300'}`} />
                  两次密码输入一致
                </li>
              </ul>
            </div>

            {/* 用户服务协议 */}
            <div className={`border rounded-lg p-4 ${errors.terms ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={agreedToTerms}
                  onChange={(e) => {
                    setAgreedToTerms(e.target.checked);
                    if (e.target.checked && errors.terms) {
                      setErrors(prev => ({ ...prev, terms: '' }));
                    }
                  }}
                  disabled={isLoading}
                  className="w-5 h-5 text-green-600 rounded cursor-pointer mt-0.5"
                />
                <div className="flex-1">
                  <label htmlFor="terms" className="text-sm text-gray-700 cursor-pointer">
                    我已阅读并同意
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-green-600 hover:text-green-700 font-medium mx-1 underline"
                    >
                      《用户服务协议及免责声明》
                    </button>
                  </label>
                </div>
              </div>
              {errors.terms && (
                <p className="mt-2 text-sm text-red-600 flex items-center ml-8">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.terms}
                </p>
              )}
            </div>

            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                {errors.general}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-sm font-medium text-white transition-all duration-200 ${isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 shadow-lg hover:shadow-xl'}`}
            >
              {isLoading ? '注册中...' : '注册'}
            </button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              已有账户？{' '}
              <button type="button" onClick={handleNavigateToLogin} className="font-medium text-green-600 hover:text-green-500 transition-colors">立即登录</button>
            </p>
          </div>
        </div>
      </div>

      {/* 用户服务协议弹窗 */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">用户服务协议及免责声明</h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 text-sm text-gray-700 space-y-4">
              <section>
                <h3 className="font-bold text-gray-900 mb-2">一、服务说明与限制</h3>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li><strong>服务性质</strong>：本平台提供DS-160表单自动填写辅助服务，不保证100%成功率</li>
                  <li><strong>非官方平台</strong>：本平台非美国政府官方平台，不能代表或保证签证申请结果</li>
                  <li><strong>用户责任</strong>：用户对提交的所有信息真实性、准确性负完全责任</li>
                </ul>
              </section>

              <section>
                <h3 className="font-bold text-gray-900 mb-2">二、法律风险免责</h3>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li><strong>无签证保证</strong>：平台不保证任何签证申请的最终结果</li>
                  <li><strong>信息真实性</strong>：用户提供虚假信息导致的法律后果（包括但不限于签证拒签、行政处罚等）由用户自行承担</li>
                  <li><strong>违法用途</strong>：禁止将服务用于欺诈、洗钱、冒充他人等违法活动，违反者需承担全部法律责任</li>
                  <li><strong>政策变更</strong>：美国签证政策、DS-160表单规则变更导致的影响不属于平台责任范围</li>
                </ul>
              </section>

              <section>
                <h3 className="font-bold text-gray-900 mb-2">三、技术风险免责</h3>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li><strong>系统故障</strong>：因技术故障、网络中断、服务器维护导致的服务中断或数据丢失，平台不承担责任</li>
                  <li><strong>第三方依赖</strong>：DS-160官网变更、维护、关闭导致的服务不可用</li>
                  <li><strong>自动化局限</strong>：自动化系统可能存在填写错误或遗漏，用户需仔细核对每一项内容</li>
                  <li><strong>不可抗力</strong>：因战争、自然灾害、政府行为等不可抗力因素导致的服务中断</li>
                </ul>
              </section>

              <section>
                <h3 className="font-bold text-gray-900 mb-2">四、隐私与数据保护</h3>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li><strong>数据收集</strong>：平台收集用户提供的个人信息、护照信息、照片等敏感数据</li>
                  <li><strong>数据存储</strong>：数据存储于平台服务器，采用加密措施但不保证绝对安全</li>
                  <li><strong>数据使用</strong>：平台仅用于提供服务，不会出售或出租给第三方（法律强制要求除外）</li>
                  <li><strong>数据删除</strong>：用户有权要求删除个人数据，但已提交至DS-160官网系统的数据无法撤回</li>
                  <li><strong>网络风险</strong>：互联网传输存在被拦截、破坏的风险，平台尽力保护但无法完全消除</li>
                </ul>
              </section>

              <section>
                <h3 className="font-bold text-gray-900 mb-2">五、费用与积分</h3>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li><strong>积分消耗</strong>：每次提交消耗积分，无论最终成功或失败</li>
                  <li><strong>不可退款</strong>：已消耗的积分不予退还或转换</li>
                  <li><strong>失败补偿</strong>：因平台系统故障导致的失败，可能给予积分补偿（非法定义务）</li>
                  <li><strong>积分有效期</strong>：积分可能有有效期限制，逾期自动作废</li>
                </ul>
              </section>

              <section>
                <h3 className="font-bold text-gray-900 mb-2">六、服务变更与终止</h3>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li><strong>服务调整</strong>：平台有权随时调整服务内容、功能、收费标准，恕不另行通知</li>
                  <li><strong>服务终止</strong>：平台有权随时暂停、限制或终止服务，包括永久终止</li>
                  <li><strong>账户封禁</strong>：违反服务条款、滥用服务、进行非法活动的用户将被永久封禁账户</li>
                </ul>
              </section>

              <section>
                <h3 className="font-bold text-gray-900 mb-2">七、免责声明</h3>
                <p className="mb-2">用户确认已充分了解本平台的性质、局限性和风险，并明确表示：</p>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li>使用本平台是用户的自愿选择，用户自行承担所有风险</li>
                  <li>用户对因使用本平台而产生的任何直接或间接损失（包括经济损失、声誉损害等）自行承担</li>
                  <li>平台对因用户提供虚假信息、违法使用服务导致的一切后果不承担责任</li>
                  <li>用户已知晓签证申请的最终决定权完全在美国相关部门，与本平台无关</li>
                </ul>
              </section>

              <section>
                <h3 className="font-bold text-gray-900 mb-2">八、争议解决</h3>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li><strong>管辖法院</strong>：因本服务产生的争议由平台所在地人民法院管辖</li>
                  <li><strong>适用法律</strong>：本协议及使用本平台的所有行为适用中华人民共和国法律</li>
                  <li><strong>协商优先</strong>：发生争议优先通过友好协商解决，协商不成可提起诉讼</li>
                </ul>
              </section>

              <section className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800"><strong>⚠️ 重要警告</strong>：您点击"同意并继续注册"按钮后，视为您已完全阅读、理解并无条件接受本协议的全部条款和免责声明。任何异议将无法改变本协议的有效性。</p>
              </section>
            </div>

            <div className="border-t px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={() => setShowTermsModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                关闭
              </button>
              <button
                onClick={() => {
                  setAgreedToTerms(true);
                  setShowTermsModal(false);
                }}
                className="px-4 py-2 text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                同意并关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 页脚 */}
      <Footer variant="simple" showCopyright={false} showFiling={false} />
    </div>
  );
};

export default RegisterForm;
